import find from 'lodash/find';
import uniqueId from 'lodash/uniqueId';
import _size from 'lodash/size';

import downloadFile from 'store/utils/downloadFile';
import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';

import {
  nameNewFile,
  getProgressPercentage,
  updateUploadingFiles,
  getNormalisedFiles,
} from 'store/utils/fileSystem';

import {
  getFiles as getFilesSelector,
  getFilesMap,
  getUploadingFiles,
  getAllFiles,
} from './selectors';

import * as types from './actionTypes';
import * as api from './api';

const genId = () => uniqueId('resources-file-');

/**
 * Get files
 */
export const getFiles = () => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.GET_RESOURCES_FILES_REQUEST,
    });
    const state = getState();
    const { data } = await api.getFilesRequest();
    const filesMap = getFilesMap(state);
    const files = getNormalisedFiles(data, filesMap, genId);

    await dispatch({
      type: types.GET_RESOURCES_FILES_SUCCESS,
      payload: {
        files,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_RESOURCES_FILES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Updated searchTerm
 */
export const updatedSearchTerm = searchTerm => ({
  type: types.UPDATE_RESOURCES_SEARCH_TERM,
  payload: {
    searchTerm,
  },
});

/**
 * Get file
 */
export const getFile = (file, isOpened = false) => async dispatch => {
  try {
    await dispatch({
      type: types.GET_RESOURCES_FILE_REQUEST,
      payload: {
        id: file.id,
      },
    });
    const { data } = await api.getFileRequest(file.path);
    await dispatch({
      type: types.GET_RESOURCES_FILE_SUCCESS,
      payload: {
        id: file.id,
        isOpened,
        openedAt: new Date().getTime(),
        data,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_RESOURCES_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

// eslint-disable-next-line
export const openFile = file => dispatch => {
  if (file && (file.contents || file.contents === '')) {
    dispatch({
      type: types.OPEN_RESOURCES_FILE,
      payload: {
        id: file.id,
      },
    });
    return;
  }

  dispatch(getFile(file));
};

/**
 * Add file
 */
export const addFile = path => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.ADD_RESOURCES_FILE_REQUEST,
    });
    await api.addFileRequest(path);

    const { id } = find(getAllFiles(getState()), { isNew: true });

    const newFile = {
      id,
      name: path,
      path,
    };

    await dispatch({
      type: types.ADD_RESOURCES_FILE_SUCCESS,
      payload: {
        file: newFile,
      },
    });

    await dispatch(openFile(newFile));

    Notify.success({
      title: 'Blob was created successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.ADD_RESOURCES_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to create Blob',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const uploadProgress = (file, dispatch, getState) => progress => {
  // DIRTY HACK: we set manually 90 as max percentage (and se manually to 100 when the file if fully uploaded)
  // to action UPLOAD_FILE_SUCCESS
  const percentage = Math.min(getProgressPercentage(progress), 90);
  const files = getUploadingFiles(getState());

  dispatch({
    type: types.UPLOAD_RESOURCES_FILE_REQUEST,
    payload: {
      file,
      percentage,
      files,
    },
  });
};

/**
 * Upload a file and auto open it in editor and make it current.
 */
export const uploadFile = file => async (dispatch, getState) => {
  const stateFiles = getUploadingFiles(getState());
  try {
    // DIRTY HACK: set manually default progress percentage to 10 for better ux in cases of small files
    await dispatch({
      type: types.SET_RESOURCES_DEFAULT_PERCENTAGE_TO_FILE,
      payload: {
        files: updateUploadingFiles(stateFiles, file, {
          percentage: 10,
          isLoading: true,
        }),
      },
    });

    const config = {
      onUploadProgress: await uploadProgress(file, dispatch, getState),
    };

    await api.uploadFileRequest(file, config);

    const { data } = await api.getFileRequest(file.name);

    const newFile = {
      id: genId(),
      name: file.name,
      path: file.name,
      contents: data,
    };

    await dispatch({
      type: types.UPLOAD_RESOURCES_FILE_SUCCESS,
      payload: {
        ...newFile,
        openedAt: new Date().getTime(),
        // DIRTY HACK: when the file is fully uploaded we set manually percentage to 100
        uploadingFiles: updateUploadingFiles(stateFiles, file, {
          percentage: 100,
          isLoading: false,
          hasError: false,
        }),
      },
    });

    await dispatch(openFile(newFile));
  } catch (error) {
    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
    await dispatch({
      type: types.UPLOAD_RESOURCES_FILE_ERROR,
      payload: {
        error: error.response.data,
        files: updateUploadingFiles(stateFiles, file, {
          isLoading: false,
          hasError: true,
          percentage: file.percentage,
        }),
      },
    });
  }
};

/**
 * Upload files
 */
export const uploadFiles = files => async (dispatch, getState) => {
  await dispatch({
    type: types.UPLOAD_RESOURCES_FILES_REQUEST,
    files,
  });

  try {
    /* eslint-disable */
    for (const file of files) {
      await dispatch(uploadFile(file));
    }

    const stateFiles = getUploadingFiles(getState());
    const loadedFiles = stateFiles.filter(f => !f.hasError);

    if (loadedFiles.length === stateFiles.length) {
      await dispatch({
        type: types.UPLOAD_RESOURCES_FILES_SUCCESS,
      });

      Notify.success({
        title: `${
          files.length === 1 ? 'Blob was' : 'Blobs were'
        } uploaded successfully`,
      });
    }
  } catch (error) {
    await dispatch({
      type: types.UPLOAD_RESOURCES_FILES_ERROR,
      payload: {
        error: 'Error',
      },
    });
  }
};

/**
 * Update current files
 */
export const updateFiles = files => async (dispatch, getState) => {
  await Promise.all(
    files.map(file => {
      getFile(file, true)(dispatch, getState);
    }),
  );
};

export const resetUploadFiles = () => dispatch => {
  dispatch({
    type: types.RESET_RESOURCES_UPLOAD_FILES,
  });
};

/**
 * Update file
 */
export const updateFile = (file, name) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.UPDATE_RESOURCES_FILE_REQUEST,
    });

    await api.updateFileRequest(file.path, { name, path: name });

    const files = getFilesSelector(getState()).map(entry => ({
      ...entry,
      path: entry.path === file.path ? name : entry.path,
      name: entry.path === file.path ? name : entry.name,
    }));

    await dispatch({
      type: types.UPDATE_RESOURCES_FILE_SUCCESS,
      payload: {
        files,
      },
    });

    Notify.success({
      title: 'Blob was updated successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.UPDATE_RESOURCES_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to update Blob',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add entry
 */
export const addNewFile = () => (dispatch, getState) => {
  const name = nameNewFile(getFilesSelector(getState()), 'New File');

  dispatch({
    type: types.ADD_RESOURCES_NEW_FILE,
    payload: {
      file: {
        id: genId(),
        name,
        path: '',
        isNew: true,
      },
    },
  });
};

/**
 * Delete entry
 */
export const deleteFile = file => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.DELETE_RESOURCES_FILE_REQUEST,
    });

    await api.deleteFileRequest(file.path);

    await dispatch({
      type: types.DELETE_RESOURCES_FILE_SUCCESS,
      payload: {
        id: file.id,
      },
    });

    Notify.success({
      title: 'Blob was deleted successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.DELETE_RESOURCES_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to delete Blob',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Export file
 */
export const exportFile = ({ path, name }) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.EXPORT_RESOURCES_FILE_REQUEST,
    });

    const { data } = await api.getFileRequest(path);

    dispatch({
      type: types.EXPORT_RESOURCES_FILE_SUCCESS,
    });

    downloadFile(data, name);
  } catch (error) {
    await dispatch({
      type: types.EXPORT_RESOURCES_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update Blob's contents
 */
export const updateBlob = (file, value, snoozeNotification = false) => async (
  dispatch,
  getState,
) => {
  try {
    await dispatch({
      type: types.UPDATE_RESOURCES_BLOB_REQUEST,
    });

    const blob = new Blob([value], { type: 'text/plain' });
    const data = new FormData();
    data.append('contents', blob);

    await api.updateBlobRequest(file.path, data);

    const files = getFilesSelector(getState());

    await dispatch({
      type: types.UPDATE_RESOURCES_BLOB_SUCCESS,
      payload: {
        file,
        contents: value,
        files,
      },
    });

    if (!snoozeNotification) {
      Notify.success({
        title: `${file.path} was updated successfully`,
      });
    }
  } catch (error) {
    await dispatch({
      type: types.UPDATE_RESOURCES_BLOB_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'Fail to update blob',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Close File
 */
export const closeEditorFile = ({ path }) => (dispatch, getState) => {
  const file = find(getAllFiles(getState()), { path });

  if (file && file.id) {
    return dispatch({
      type: types.CLOSE_RESOURCES_FILE,
      payload: {
        id: file.id,
      },
    });
  }
};

/**
 * Update editor searchTerm
 */
export const updateEditorSearchTerm = searchTerm => ({
  type: types.UPDATE_RESOURCES_EDITOR_SEARCH_TERM,
  payload: {
    searchTerm,
  },
});

/**
 * set theme
 */
export const setTheme = theme => ({
  type: types.SET_RESOURCES_THEME,
  payload: {
    theme: theme,
  },
});

/**
 * Delete multiple files
 */
export const deleteFiles = (files = []) => async (dispatch, getState) => {
  try {
    const deletedIds = files.map(file => file.id);

    dispatch({
      type: types.DELETE_RESOURCES_FILES_REQUEST,
    });

    await api.deleteFilesRequest({
      delete: files.map(file => file.path),
    });

    dispatch({
      type: types.DELETE_RESOURCES_FILES_SUCCESS,
      payload: {
        deletedIds,
      },
    });

    Notify.success({
      title: `${
        _size(deletedIds) > 1 ? 'Files were' : 'file was'
      } deleted successfully`,
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_RESOURCES_FILES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Export multiple files
 */
export const exportFiles = (files = []) => async (dispatch, getState) => {
  try {
    const paths = files.map(file => file.path);

    dispatch({
      type: types.EXPORT_RESOURCES_FILES_REQUEST,
    });

    const { data } = await api.exportFilesRequest({
      paths,
    });

    downloadFile(data, 'resources.zip');

    dispatch({
      type: types.EXPORT_RESOURCES_FILES_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: types.EXPORT_RESOURCES_FILES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Open files
 */
export const openFiles = (files = []) => async (dispatch, getState) => {
  dispatch({
    type: types.OPEN_RESOURCES_FILES_REQUEST,
  });

  try {
    /* eslint-disable */
    for (const file of files) {
      await dispatch(openFile(file));
    }

    dispatch({
      type: types.OPEN_RESOURCES_FILES_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: types.OPEN_RESOURCES_FILES_ERROR,
      payload: {
        error: 'Error',
      },
    });
  }
};
